import React, { useState, useRef, useEffect } from 'react';
import { renderMoney } from 'common/services/util';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';

const PRIMARY = '#3f51b5';

const InvoiceSummaryPopup = ({ data, tasksSelected, tableRef, filterRow }) => {
    const [collapsed, setCollapsed] = useState(false);
    const [pos, setPos] = useState(null); // null = posición inicial centrada
    const dragging = useRef(false);
    const hasMoved = useRef(false);
    const dragOffset = useRef({ x: 0, y: 0 });
    const popupRef = useRef(null);

    const dm = tableRef?.current?.dataManager;
    const searchedData = dm?.searchedData || data;
    const page = dm?.currentPage ?? 0;
    const pageSize = dm?.pageSize ?? searchedData.length;
    const pagedData = searchedData.slice(page * pageSize, (page + 1) * pageSize);
    const base = tasksSelected?.length > 0 ? tasksSelected : pagedData;
    const dataToSum = filterRow ? base.filter(filterRow) : base;
    const isSelection = tasksSelected?.length > 0;
    const count = dataToSum.length;

    const sumTotal = dataToSum.reduce((acc, inv) => acc + (Number(inv.totalPrice) || 0), 0);
    const sumBase  = dataToSum.reduce((acc, inv) => acc + (Number(inv.basePrice)  || 0), 0);

    const handleMouseDown = (e) => {
        if (e.target.closest('svg')) return;
        e.preventDefault();

        const rect = popupRef.current.getBoundingClientRect();
        dragOffset.current = {
            x: e.clientX - rect.left,
            y: e.clientY - rect.top,
        };
        dragging.current = true;
        hasMoved.current = false;
        popupRef.current.style.cursor = 'grabbing';
    };

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!dragging.current) return;
            hasMoved.current = true;
            setPos({
                x: e.clientX - dragOffset.current.x,
                y: e.clientY - dragOffset.current.y,
            });
        };

        const handleMouseUp = () => {
            if (!dragging.current) return;
            dragging.current = false;
            if (popupRef.current) popupRef.current.style.cursor = '';
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, []);

    const positionStyle = pos
        ? { left: pos.x, top: pos.y, bottom: 'auto', transform: 'none' }
        : { bottom: 24, left: '50%', transform: 'translateX(-50%)' };

    return (
        <div
            ref={popupRef}
            style={{
                position: 'fixed',
                ...positionStyle,
                zIndex: 1300,
                background: 'white',
                borderRadius: 8,
                boxShadow: '0 4px 20px rgba(0,0,0,0.18)',
                border: `1.5px solid ${PRIMARY}`,
                fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
                minWidth: 280,
                overflow: 'hidden',
                userSelect: 'none',
            }}
        >
            {/* Header — zona de arrastre */}
            <div
                onMouseDown={handleMouseDown}
                onClick={() => { if (!hasMoved.current) setCollapsed(c => !c); }}
                style={{
                    background: PRIMARY,
                    color: 'white',
                    padding: '6px 14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'grab',
                    fontSize: '0.8rem',
                    fontWeight: 500,
                    letterSpacing: '0.02em',
                }}
            >
                <span>
                    {isSelection
                        ? `${count} seleccionada${count !== 1 ? 's' : ''}`
                        : `${count} filtrada${count !== 1 ? 's' : ''}`}
                </span>
                {collapsed ? <ExpandLessIcon style={{ fontSize: 18 }} /> : <ExpandMoreIcon style={{ fontSize: 18 }} />}
            </div>

            {/* Body */}
            {!collapsed && (
                <div style={{
                    display: 'flex',
                    gap: 24,
                    padding: '10px 18px',
                    alignItems: 'center',
                }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <span style={{ fontSize: '0.7rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Base</span>
                        <span style={{ fontSize: '1rem', fontWeight: 700, color: PRIMARY }}>{renderMoney(sumBase)}</span>
                    </div>
                    <div style={{ width: 1, height: 32, background: '#e0e0e0' }} />
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <span style={{ fontSize: '0.7rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total</span>
                        <span style={{ fontSize: '1rem', fontWeight: 700, color: PRIMARY }}>{renderMoney(sumTotal)}</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InvoiceSummaryPopup;
